import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import { useFormData } from './hooks';
import { FormProvider, RHFTextField, RHFAutocomplete } from 'src/components/minimals/form';
import { DisplayedAssetProperties } from './components/DisplayedAssetProperties';
import { useAssetsContext } from 'src/features/sites/contexts/assetsContext/assetsContext';
import { useControllersContext } from 'src/features/sites/contexts/controllersContext';
import { LiveDataTagsSection } from './components/LiveDataTagsSection';
import { ScrollbarWrapper } from 'src/components';
import { WidgetType, WidgetTypeEnum } from 'src/features/sites/types/types';
import { AssetType } from 'src/types/assets';
import { useDraggableList } from 'src/hooks/useDraggableList';
import { useAuthentication } from 'src/features/authentication/context';
import { WidgetService } from 'src/services/widgets/widgets.service';
import { AssetService } from 'src/services/assets/asset.service';
import { InfoIconV2 } from 'src/assets/icons/InfoIconV2';
import { useResponsive } from 'src/hooks/useResponsive';
import { TagResponse } from 'src/types/tags/tags';

type Props = {
  gatewayInputs: TagResponse[];
  isOpen: boolean;
  onToggle: () => void;
  position: {
    x: number;
    y: number;
  };
  isEdit?: boolean;
  existingWidget?: WidgetType;
  toggleAddOrRemoveHandler: () => void;
  selectedWidgetSubtype: string;
};

export const LiftStationWidgetPopup = ({
  gatewayInputs,
  isOpen,
  onToggle,
  position,
  isEdit,
  existingWidget,
  toggleAddOrRemoveHandler,
  selectedWidgetSubtype,
}: Props) => {
  const { methods, handleSubmit, values } = useFormData();

  const { wetWellAssets, pumpAssets } = useAssetsContext();
  const { controllers } = useControllersContext();

  const isTriplexPump = selectedWidgetSubtype === 'TRIPLEX_SUBMERGED';
  const isQuadruplexPump = selectedWidgetSubtype === 'QUADRUPLEX_SUBMERGED';

  const initialRows = useMemo(() => {
    if (isEdit && existingWidget?.connectedTo?.liveDataTags && gatewayInputs?.length > 0) {
      const mappedRows = existingWidget?.connectedTo?.liveDataTags?.map(
        (tag: any, index: number) => ({
          id: `row-${index}`,
          position: tag.position,
          tagId: tag.id,
          tag: gatewayInputs.find((t) => t.id === tag.id) ?? null,
        })
      );
      return { value: mappedRows, loaded: true };
    }
    return { value: [], loaded: false };
  }, [isEdit, existingWidget, gatewayInputs]);

  const [findWetWell, setFindWetWell] = useState<AssetType | null>(null);
  const [findLeftPump, setFindLeftPump] = useState<AssetType | null>(null);
  const [findRightPump, setFindRightPump] = useState<AssetType | null>(null);
  const [findThirdPump, setFindThirdPump] = useState<AssetType | null>(null);
  const [findFourthPump, setFindFourthPump] = useState<AssetType | null>(null);

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const getAssetPayload = (
    assetData: AssetType,
    type: 'wetWell' | 'leftPump' | 'rightPump' | 'thirdPump' | 'fourthPump'
  ) => {
    if (!assetData?.id) return null;

    const selectedCheckboxes = values.assetProperties?.[type] || {};

    const allTags = [
      ...(assetData.assignedAlarmTags ?? []).map((tag: any) => ({
        id: tag.id,
        tagName: tag.alarmTagName,
        assignedTagId: tag.assignedAlarmTagId,
      })),

      ...(assetData.assignedTags ?? [])
        .filter((tag: any) => tag.tagName === 'Mode' || tag.tagName === 'State')
        .map((tag: any) => ({
          id: tag.id,
          tagName: tag.tagName,
          assignedTagId: tag.assignedTagId,
        })),
    ];

    const assetProperties = allTags.filter(
      (tag) => tag.assignedTagId && selectedCheckboxes[tag.id]
    );

    return {
      id: assetData.id,
      assetName: assetData.assetName,
      assetProperties: assetProperties.map((tag) => ({
        tag_name: tag.tagName,
        id: tag.id,
        assigned_tag_id: tag.assignedTagId,
      })),
    };
  };

  const {
    addRow,
    rows,
    onDragEnd,
    handleSetRow,
    removeRow,
    setMobileListWidth,
    selectedTags,
    setRows,
  } = useDraggableList({
    isLiveDataWidget: true,
    initialRows,
    selectedSite: siteId,
  });

  useEffect(() => {
    if (!isEdit && isOpen) {
      methods.reset();
      setFindWetWell(null);
      setFindLeftPump(null);
      setFindRightPump(null);
      setFindThirdPump(null);
      setFindFourthPump(null);
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, isOpen]);

  useEffect(() => {
    if (!isEdit || !isOpen || !existingWidget) return;

    const wetWellReady = wetWellAssets && Array.isArray(wetWellAssets);
    const pumpReady = pumpAssets && Array.isArray(pumpAssets);
    const controllersReady = controllers && Array.isArray(controllers);

    if (!wetWellReady || !pumpReady || !controllersReady) return;

    methods.reset();
    setFindWetWell(null);
    setFindLeftPump(null);
    setFindRightPump(null);
    setFindThirdPump(null);
    setFindFourthPump(null);
    setRows([]);

    const wetWell =
      wetWellAssets.find((w) => w.id === existingWidget?.connectedTo?.wetWell?.id) ?? null;
    const leftPump =
      pumpAssets.find((p) => p.id === existingWidget?.connectedTo?.leftPump?.id) ?? null;
    const rightPump =
      pumpAssets.find((p) => p.id === existingWidget?.connectedTo?.rightPump?.id) ?? null;
    const thirdPump =
      pumpAssets.find((p) => p.id === existingWidget?.connectedTo?.thirdPump?.id) ?? null;
    const fourthPump =
      pumpAssets.find((p) => p.id === existingWidget?.connectedTo?.fourthPump?.id) ?? null;
    const controller =
      controllers.find((c) => c.id === existingWidget?.connectedTo?.controller?.id) ?? null;

    methods.setValue('title', existingWidget.title ?? '');
    methods.setValue('wetWell', wetWell);
    methods.setValue('leftPump', leftPump);
    methods.setValue('rightPump', rightPump);
    methods.setValue('thirdPump', thirdPump);
    methods.setValue('fourthPump', fourthPump);
    methods.setValue('controller', controller);

    if (wetWell?.id) fetchAssetDetails(wetWell.id, 'wetWell');
    if (leftPump?.id) fetchAssetDetails(leftPump.id, 'leftPump');
    if (rightPump?.id) fetchAssetDetails(rightPump.id, 'rightPump');
    if (thirdPump?.id) fetchAssetDetails(thirdPump.id, 'thirdPump');
    if (fourthPump?.id) fetchAssetDetails(fourthPump.id, 'fourthPump');

    const assetPropsMap = {
      wetWell: {},
      leftPump: {},
      rightPump: {},
      thirdPump: {},
      fourthPump: {},
    };

    ['wetWell', 'leftPump', 'rightPump', 'thirdPump', 'fourthPump'].forEach((type) => {
      const connectedAsset = existingWidget?.connectedTo?.[type];
      connectedAsset?.assetProperties?.forEach((tag: any) => {
        if (tag?.id) {
          assetPropsMap[type][tag.id] = true;
        }
      });
    });

    methods.setValue('assetProperties', assetPropsMap);

    const mappedRows =
      existingWidget.connectedTo?.liveDataTags?.map((tag: any, index: number) => ({
        id: `row-${index}`,
        position: tag.position,
        tagId: tag.id,
        tag: gatewayInputs?.find((t) => t.id === tag.id) ?? null,
      })) ?? [];

    setRows(mappedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, isOpen, existingWidget, wetWellAssets, pumpAssets, controllers]);

  const isMobile = useResponsive('down', 'md');
  const fieldSize = isMobile ? 'small' : 'medium';

  const onDiscardHandler = () => {
    methods.reset();
    setFindWetWell(null);
    setFindLeftPump(null);
    setFindRightPump(null);
    setFindThirdPump(null);
    setFindFourthPump(null);
    onToggle();
  };

  const removeNulls = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(removeNulls);
    } else if (obj !== null && typeof obj === 'object') {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        const cleanedValue = removeNulls(value);
        if (cleanedValue !== null && cleanedValue !== undefined) {
          acc[key] = cleanedValue;
        }
        return acc;
      }, {} as any);
    }
    return obj;
  };

  const payloadData = {
    title: values.title,
    widgetType: WidgetTypeEnum.LIFT_STATION_WIDGET,
    widgetSubType: selectedWidgetSubtype,
    positioning:
      isEdit && existingWidget?.positioning
        ? existingWidget.positioning
        : {
            x: position?.x,
            y: position?.y,
            width: 4,
            height: 4.4,
          },
    connectedTo: {
      leftPump: getAssetPayload(findLeftPump, 'leftPump'),
      rightPump: getAssetPayload(findRightPump, 'rightPump'),
      thirdPump: getAssetPayload(findThirdPump, 'thirdPump'),
      fourthPump: getAssetPayload(findFourthPump, 'fourthPump'),
      wetWell: getAssetPayload(findWetWell, 'wetWell'),
      controller: values.controller?.id ? { id: values.controller.id } : null,
      liveDataTags: rows
        .filter((row) => row.tag?.tagId || row.tagId)
        .map((row, index) => ({
          id: row.tag?.tagId || row.tagId,
          position: index,
        })),
    },
  };

  const onSubmitHandler = async () => {
    if (!customerId || !siteId) return;

    try {
      const cleanedPayload = removeNulls(payloadData);

      if (isEdit && existingWidget?.id) {
        await WidgetService.edit({
          widgetId: existingWidget.id,
          customerId,
          siteId,
          data: cleanedPayload,
        });
      } else {
        await WidgetService.add({
          title: values.title,
          customerId,
          siteId,
          data: cleanedPayload,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      onToggle();
      toggleAddOrRemoveHandler();
    }
  };

  const fetchAssetDetails = async (
    assetId: string,
    type: 'wetWell' | 'leftPump' | 'rightPump' | 'thirdPump' | 'fourthPump'
  ) => {
    if (!customerId || !siteId) return;
    try {
      const data = await AssetService.getAssetById({
        customerId,
        siteId,
        assetId,
      });

      if (type === 'wetWell') setFindWetWell(data);
      if (type === 'leftPump') setFindLeftPump(data);
      if (type === 'rightPump') setFindRightPump(data);
      if (type === 'thirdPump') setFindThirdPump(data);
      if (type === 'fourthPump') setFindFourthPump(data);
    } catch (err) {
      console.error(`Failed to fetch ${type}`, err);
    }
  };

  return (
    <Dialog
      fullWidth
      onClose={onDiscardHandler}
      open={isOpen}
      sx={{ display: 'flex', justifyContent: 'center' }}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? '100% !important' : '600px',
          margin: 0,
          maxWidth: isMobile ? '350px !important' : '100%',
        },
      }}
    >
      <ScrollbarWrapper>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            paddingRight: isMobile ? '20px' : '60px',
            maxWidth: '100%',
          }}
        >
          <DialogTitle sx={{ fontSize: isMobile ? '16px' : '18px' }}>
            Assign Assets to Widget Elements
          </DialogTitle>
          <Tooltip
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 10],
                  },
                },
              ],
              sx: {
                '& .MuiTooltip-tooltip': {
                  maxWidth: '320px',
                  fontSize: '13px',
                },
              },
            }}
            title="Assign assets to the Lift Station widget. Note: Only mapped properties will be available to be shown."
            placement="right"
            arrow
            disableInteractive
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <InfoIconV2 />
            </span>
          </Tooltip>
        </Box>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitHandler)}>
          <DialogContent
            sx={{
              padding: '10px 24px 16px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
            }}
          >
            <RHFTextField
              name="title"
              label="Widget name"
              testId="Widget-name-input"
              sx={{ width: '100%' }}
              size={fieldSize}
            />
            <RHFAutocomplete
              name="wetWell"
              disableClearable
              options={wetWellAssets}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option?.assetName ?? ''
              }
              isOptionEqualToValue={(option, value) =>
                !!option && !!value && option.id === value.id
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Wet Well"
                  sx={{ width: '100%' }}
                  size={fieldSize}
                />
              )}
              onChange={(_, value) => {
                methods.setValue('wetWell', value);
                if (value?.id) {
                  fetchAssetDetails(value.id, 'wetWell');
                }
              }}
            />
            {findWetWell && (
              <Box sx={{ pl: 4, mt: -2 }}>
                <DisplayedAssetProperties
                  typeOfAssetForMessage="Wet Well"
                  typeOfAsset="wetWell"
                  assetProperties={(findWetWell.assignedAlarmTags ?? []).filter(
                    (tag) => tag.assignedAlarmTagId != null
                  )}
                />
              </Box>
            )}
            <RHFAutocomplete
              sx={{ width: '100%' }}
              name="leftPump"
              disableClearable
              options={pumpAssets}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option?.assetName ?? ''
              }
              isOptionEqualToValue={(option, value) =>
                !!option && !!value && option.id === value.id
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.assetName}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Left Pump" size={fieldSize} />
              )}
              onChange={(_, value) => {
                methods.setValue('leftPump', value);
                if (value?.id) {
                  fetchAssetDetails(value.id, 'leftPump');
                }
              }}
            />
            {findLeftPump && (
              <Box sx={{ pl: 4, mt: -2 }}>
                <DisplayedAssetProperties
                  typeOfAssetForMessage="Left Pump"
                  typeOfAsset="leftPump"
                  assetProperties={[
                    ...(findLeftPump.assignedAlarmTags ?? []).filter(
                      (tag) => tag.alarmTagName === 'State' && tag.assignedAlarmTagId != null
                    ),
                    ...(findLeftPump.assignedTags ?? [])
                      .filter((tag) => tag.tagName === 'State' && tag.assignedTagId != null)
                      .map((tag) => ({
                        alarmTagName: tag.tagName,
                        id: tag.id,
                        assignedAlarmTagId: tag.assignedTagId,
                      })),
                  ]}
                />
              </Box>
            )}
            {(isTriplexPump || isQuadruplexPump) && (
              <RHFAutocomplete
                name="thirdPump"
                disableClearable
                options={pumpAssets}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option?.assetName ?? ''
                }
                isOptionEqualToValue={(option, value) =>
                  !!option && !!value && option.id === value.id
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.assetName}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={isTriplexPump ? 'Center Pump' : 'Center Left Pump'}
                    size={fieldSize}
                  />
                )}
                onChange={(_, value) => {
                  methods.setValue('thirdPump', value);
                  if (value?.id) {
                    fetchAssetDetails(value.id, 'thirdPump');
                  }
                }}
              />
            )}
            {findThirdPump && (
              <Box sx={{ pl: 4, mt: -2 }}>
                <DisplayedAssetProperties
                  methods={methods}
                  values={values}
                  typeOfAssetForMessage={isTriplexPump ? 'Center Pump' : 'Center Left Pump'}
                  typeOfAsset="thirdPump"
                  assetProperties={[
                    ...(findThirdPump.assignedAlarmTags ?? []).filter(
                      (tag) => tag.alarmTagName === 'State' && tag.assignedAlarmTagId != null
                    ),
                    ...(findThirdPump.assignedTags ?? [])
                      .filter((tag) => tag.tagName === 'State' && tag.assignedTagId != null)
                      .map((tag) => ({
                        alarmTagName: tag.tagName,
                        id: tag.id,
                        assignedAlarmTagId: tag.assignedTagId,
                      })),
                  ]}
                />
              </Box>
            )}
            {/**---------------Fourth pump ---------------------------*/}
            {isQuadruplexPump && (
              <RHFAutocomplete
                name="fourthPump"
                disableClearable
                options={pumpAssets}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option?.assetName ?? ''
                }
                isOptionEqualToValue={(option, value) =>
                  !!option && !!value && option.id === value.id
                }
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.assetName}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Center Right Pump" size={fieldSize} />
                )}
                onChange={(_, value) => {
                  methods.setValue('fourthPump', value);
                  if (value?.id) {
                    fetchAssetDetails(value.id, 'fourthPump');
                  }
                }}
              />
            )}
            {findFourthPump && (
              <Box sx={{ pl: 4, mt: -2 }}>
                <DisplayedAssetProperties
                  methods={methods}
                  values={values}
                  typeOfAssetForMessage="Center Right Pump"
                  typeOfAsset="fourthPump"
                  assetProperties={[
                    ...(findFourthPump.assignedAlarmTags ?? []).filter(
                      (tag) => tag.alarmTagName === 'State' && tag.assignedAlarmTagId != null
                    ),
                    ...(findFourthPump.assignedTags ?? [])
                      .filter((tag) => tag.tagName === 'State' && tag.assignedTagId != null)
                      .map((tag) => ({
                        alarmTagName: tag.tagName,
                        id: tag.id,
                        assignedAlarmTagId: tag.assignedTagId,
                      })),
                  ]}
                />
              </Box>
            )}

            {/**----------------------------------------*/}
            <RHFAutocomplete
              name="rightPump"
              disableClearable
              options={pumpAssets}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option?.assetName ?? ''
              }
              isOptionEqualToValue={(option, value) =>
                !!option && !!value && option.id === value.id
              }
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.assetName}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Right Pump" size={fieldSize} />
              )}
              onChange={(_, value) => {
                methods.setValue('rightPump', value);
                if (value?.id) {
                  fetchAssetDetails(value.id, 'rightPump');
                }
              }}
            />
            {findRightPump && (
              <Box sx={{ pl: 4, mt: -2 }}>
                <DisplayedAssetProperties
                  methods={methods}
                  values={values}
                  typeOfAssetForMessage="Right Pump"
                  typeOfAsset="rightPump"
                  assetProperties={[
                    ...(findRightPump.assignedAlarmTags ?? []).filter(
                      (tag) => tag.alarmTagName === 'State' && tag.assignedAlarmTagId != null
                    ),
                    ...(findRightPump.assignedTags ?? [])
                      .filter((tag) => tag.tagName === 'State' && tag.assignedTagId != null)
                      .map((tag) => ({
                        alarmTagName: tag.tagName,
                        id: tag.id,
                        assignedAlarmTagId: tag.assignedTagId,
                      })),
                  ]}
                />
              </Box>
            )}

            <RHFAutocomplete
              name="controller"
              disableClearable
              options={controllers}
              getOptionLabel={(option) => option?.controllerName || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} fullWidth label="Controller" size={fieldSize} />
              )}
              onChange={(_, value) => {
                methods.setValue('controller', value);
              }}
            />

            <LiveDataTagsSection
              gatewayInputs={gatewayInputs}
              addRow={addRow}
              rows={rows}
              onDragEnd={onDragEnd}
              handleSetRow={handleSetRow}
              removeRow={removeRow}
              setMobileListWidth={setMobileListWidth}
              selectedTags={selectedTags}
            />
          </DialogContent>
          <DialogActions>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <Button
                variant="outlined"
                data-sm="discard-lift-station-popup-button"
                onClick={onDiscardHandler}
              >
                Discard
              </Button>
              <Button type="submit" variant="contained" data-sm="apply-lift-station-popup-button">
                Confirm
              </Button>
            </Box>
          </DialogActions>
        </FormProvider>
      </ScrollbarWrapper>
    </Dialog>
  );
};
